import React, { useEffect, useRef, useState } from 'react';
import { Panel } from '@/components/ui/panel';
import {
	Box,
	createListCollection,
	HStack,
	Icon,
	ScrollArea,
	Select,
	Stack,
	Switch,
	Text,
	VisuallyHidden,
	VStack,
} from '@chakra-ui/react';
import { LuRepeat } from 'react-icons/lu';

import {
	FREQUENCY_INTERVALS,
	FrequencySelection,
	FrequencyValue,
	getFrequencyEveryOptions,
	mapFrequencySelectionToString,
	toFrequencySelection,
} from '@/utils/maintenance/frequencyUtils';
import { dropdownScrollbarCss, hideNativeScrollbarCss } from '@/components/ui/scrollbar.ts';
import { SELECT_TRIGGER_PROPS } from '@/components/ui/inputStyles.ts';
import {
	COMPOSER_BAR_CONTROL_H,
	COMPOSER_REPEAT_WHEN_COMPLETED_LABEL,
} from '@/components/common/composer/composerConstants.ts';

// Measure the frequency column itself: the submit cluster (and feature-flagged sub-task button)
// has already taken its space. No resize observer or viewport breakpoint is needed.
const FREQUENCY_CONTAINER = 'maintenance-frequency-row';
const FREQUENCY_FULL_LABEL = `@container ${FREQUENCY_CONTAINER} (min-width: 340px)`;
const FREQUENCY_ICON_LABEL = `@container ${FREQUENCY_CONTAINER} (min-width: 240px)`;
const FREQUENCY_CONTAINER_CSS = {
	containerType: 'inline-size',
	containerName: FREQUENCY_CONTAINER,
} as const;
const FREQUENCY_ROOMY_ROW_CSS = {
	[FREQUENCY_FULL_LABEL]: { gap: '24px', paddingLeft: '32px' },
} as const;
const FREQUENCY_FULL_LABEL_CSS = {
	display: 'none',
	[FREQUENCY_FULL_LABEL]: { display: 'inline' },
} as const;
const FREQUENCY_SHORT_LABEL_CSS = {
	[FREQUENCY_FULL_LABEL]: { display: 'none' },
} as const;
const FREQUENCY_ICON_CSS = {
	display: 'none',
	[FREQUENCY_ICON_LABEL]: { display: 'inline-block' },
} as const;

// Floating UI measures the room around the trigger after accounting for clipping ancestors such as
// the review dialog. Use that full height: this is a ceiling only when the natural list would exceed
// the room, rather than an arbitrary cap on a menu that already fits.
//
// TODO(maintenance-frequency-short-viewport): An extremely short, desktop-width review drawer can
// still clip the top of this menu after it flips above the trigger. The select stays non-portalled so
// it remains inside the review dialog's focus trap, but the transformed/overflow-clipped drawer can
// then become a tighter boundary than the positioner's available-height measurement. Fix this by
// portalling into a focus-trap-owned container or by passing the review surface's measured bounds.
// Once the menu is contained by those real bounds, show a blurred chevron at each edge that still
// has hidden options and auto-scroll in that direction while it is hovered. The edge affordance
// cannot replace the bounds fix because it would itself be clipped with the menu.
const COUNT_MENU_MAX_H = 'var(--available-height, 60vh)';
const SELECT_CONTENT_INSET = 2;

const CountMenuScrollArea: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<ScrollArea.Root maxH={COUNT_MENU_MAX_H} size="xs" variant="hover">
		<ScrollArea.Viewport maxH={COUNT_MENU_MAX_H} css={hideNativeScrollbarCss}>
			<ScrollArea.Content p={SELECT_CONTENT_INSET}>{children}</ScrollArea.Content>
		</ScrollArea.Viewport>
		<ScrollArea.Scrollbar position="absolute" insetBlock={1} right={1} m={0} zIndex={1} bg="transparent" />
	</ScrollArea.Root>
);

interface FrequencySelectorProps {
	value?: string;
	onChange: (frequency: string) => void;
	error?: string;
	isRecurring?: boolean;
	onRecurringChange?: (isRecurring: boolean) => void;
	showToggle?: boolean;
	/** Lay the label + selects out on one row (desktop) for the compact composer reveal. */
	inline?: boolean;
	/**
	 * The inline fields as their own box rather than a row of a larger form.
	 *
	 * Two things go together for a surface that exists only to hold this field — the review card's
	 * popover and sheet. It drops the left indent, which dates from when these sat under the
	 * recurrence switch and lines up with nothing once `showToggle` is false. And it keeps the label
	 * on the pills' row at every width instead of stacking it above them on a phone, because a sheet
	 * opened to change one thing should not spend two rows saying what the thing is.
	 *
	 * Off by default, so the composer keeps the layout it was built with.
	 */
	compact?: boolean;
	/** Draw the surrounding card. Off where the control already sits on one, like an action sheet. */
	framed?: boolean;
	/** Keep the count list inside its measured clipping boundary when hosted by a review editor. */
	fitCountMenuToAvailableSpace?: boolean;
}

/**
 * Component for selecting maintenance task frequency
 */
const FrequencySelector: React.FC<FrequencySelectorProps> = (props) => {
	const {
		value,
		onChange,
		error,
		isRecurring = true,
		onRecurringChange,
		showToggle = true,
		inline = false,
		compact = false,
		framed = true,
		fitCountMenuToAvailableSpace = false,
	} = props;
	const [selection, setSelection] = useState<FrequencySelection>({
		every: 1,
		interval: 'month',
	});
	const isInitializedRef = useRef(false);

	// Create number options (1-12, adjustable based on interval)
	const createNumberCollection = (values: number[]) =>
		createListCollection({
			items: values.map((value) => ({
				label: value.toString(),
				value: value.toString(),
			})),
		});

	const intervalCollection = createListCollection({
		items: FREQUENCY_INTERVALS.map((interval) => ({
			// Singular for "every 1 month", plural otherwise -- the "(s)" toggles on the count.
			label: interval.label.replace('(s)', selection.every === 1 ? '' : 's'),
			value: interval.value,
		})),
	});

	// Initialize component state based on current value
	useEffect(() => {
		if (!value) return;

		// Named frequencies resolve to the cadence they stand for, so "quarterly" shows as every 3
		// months rather than defaulting the controls to every 1 month.
		const selected = toFrequencySelection(value);
		if (selected) {
			setSelection(selected);
		}
		isInitializedRef.current = true;
	}, [value]);

	const handleEveryChange = (details: { value: string[] }) => {
		if (details.value.length > 0) {
			const every = parseInt(details.value[0]) || 1;
			const newSelection = { ...selection, every };
			setSelection(newSelection);
			onChange(mapFrequencySelectionToString(newSelection));
		}
	};

	const handleIntervalChange = (details: { value: string[] }) => {
		if (details.value.length > 0) {
			const interval = details.value[0] as FrequencySelection['interval'];
			const newSelection = { ...selection, interval };
			setSelection(newSelection);
			onChange(mapFrequencySelectionToString(newSelection));
		}
	};

	const handleRecurringChange = (checked: boolean) => {
		if (onRecurringChange) {
			onRecurringChange(checked);
		}
		if (!checked) {
			onChange(FrequencyValue.Once);
			return;
		}
		onChange(mapFrequencySelectionToString(selection));
	};

	// Keep the ordinary range bounded, then append one saved out-of-range value when necessary.
	// That preserves "every 18 months" without letting free-text API data create a huge menu.
	const numberCollection = createNumberCollection(getFrequencyEveryOptions(selection));
	const selectContentProps = {
		borderRadius: '2xl',
		p: 2,
		maxH: 'none',
		overflowY: 'visible',
		css: dropdownScrollbarCss,
	} as const;
	const numberItems = numberCollection.items.map((item) => (
		<Select.Item item={item} key={item.value} borderRadius="xl">
			{item.label}
			<Select.ItemIndicator />
		</Select.Item>
	));

	if (!showToggle && !isRecurring) return null;

	const numberSelect = (
		<Select.Root
			collection={numberCollection}
			value={[selection.every.toString()]}
			onValueChange={handleEveryChange}
			positioning={{ strategy: 'fixed', hideWhenDetached: true }}
			w="fit-content"
		>
			<Select.HiddenSelect />
			<Select.Control>
				<Select.Trigger
					{...SELECT_TRIGGER_PROPS}
					aria-label="Repeat count"
					w="auto"
					px={inline ? 3 : 4}
					h={inline ? COMPOSER_BAR_CONTROL_H : undefined}
					minH={inline ? COMPOSER_BAR_CONTROL_H : undefined}
				>
					{/* Chevron in the flow so an auto-width trigger measures it; space-between still
					    parks it on the right edge when the trigger is stretched instead. */}
					<HStack gap={2} flex={1} minW={0} justify="space-between" whiteSpace="nowrap">
						<Select.ValueText placeholder="1" />
						<Select.Indicator />
					</HStack>
				</Select.Trigger>
			</Select.Control>
			<Select.Positioner>
				{fitCountMenuToAvailableSpace ? (
					<Select.Content borderRadius="2xl" p={0} maxH={COUNT_MENU_MAX_H} overflow="hidden">
						<CountMenuScrollArea>{numberItems}</CountMenuScrollArea>
					</Select.Content>
				) : (
					<Select.Content {...selectContentProps}>{numberItems}</Select.Content>
				)}
			</Select.Positioner>
		</Select.Root>
	);

	const intervalSelect = (
		<Select.Root
			collection={intervalCollection}
			value={[selection.interval]}
			onValueChange={handleIntervalChange}
			positioning={{ strategy: 'fixed', hideWhenDetached: true }}
			w="fit-content"
		>
			<Select.HiddenSelect />
			<Select.Control>
				<Select.Trigger
					{...SELECT_TRIGGER_PROPS}
					aria-label="Repeat interval"
					w="auto"
					px={inline ? 3 : 4}
					h={inline ? COMPOSER_BAR_CONTROL_H : undefined}
					minH={inline ? COMPOSER_BAR_CONTROL_H : undefined}
				>
					<HStack gap={2} flex={1} minW={0} justify="space-between" whiteSpace="nowrap">
						<Select.ValueText placeholder="Months" />
						<Select.Indicator />
					</HStack>
				</Select.Trigger>
			</Select.Control>
			<Select.Positioner>
				<Select.Content {...selectContentProps}>
					{intervalCollection.items.map((item) => (
						<Select.Item item={item} key={item.value} borderRadius="xl">
							{item.label}
							<Select.ItemIndicator />
						</Select.Item>
					))}
				</Select.Content>
			</Select.Positioner>
		</Select.Root>
	);

	const errorText = !!error && (
		<Text textStyle="micro" color="text.error">
			{error}
		</Text>
	);

	// Contract Repeat every -> repeat icon + Every -> Every; retain the indent only in the full band.
	// Keep the gap between the selects unchanged; wrapping remains the extreme-narrow fallback.
	const repeatFields = inline ? (
		<VStack align="stretch" gap={2} css={compact ? undefined : FREQUENCY_CONTAINER_CSS}>
			<Stack
				direction="row"
				wrap={compact ? undefined : 'wrap'}
				gap={compact ? 3 : 2}
				align="center"
				css={compact ? undefined : FREQUENCY_ROOMY_ROW_CSS}
			>
				{compact ? (
					<Text as="label" color="brand.fg" fontWeight="medium" flexShrink={0}>
						Repeat every
					</Text>
				) : (
					<Box as="label" data-testid="frequency-label" color="brand.fg" fontWeight="medium" flexShrink={0}>
						<VisuallyHidden>Repeat every</VisuallyHidden>
						<Text as="span" aria-hidden="true" css={FREQUENCY_FULL_LABEL_CSS}>
							Repeat every
						</Text>
						<HStack as="span" aria-hidden="true" gap={1} css={FREQUENCY_SHORT_LABEL_CSS}>
							<Icon as={LuRepeat} boxSize={4} css={FREQUENCY_ICON_CSS} />
							<Text as="span">Every</Text>
						</HStack>
					</Box>
				)}
				<HStack gap={compact ? { base: 1.5, md: 2 } : 2} flexShrink={0} minW={0} w="auto">
					<Box flexShrink={0}>{numberSelect}</Box>
					<Box flexShrink={0}>{intervalSelect}</Box>
				</HStack>
			</Stack>
			{errorText}
		</VStack>
	) : (
		<VStack
			align="stretch"
			gap={2}
			pt={showToggle ? 1 : 0}
			borderTop={showToggle ? '1px solid' : undefined}
			borderColor="border.subtle"
		>
			<HStack gap={2} align="center" pt={showToggle ? 3 : 0} minW={0}>
				<Text as="label" color="brand.fg" fontWeight="medium" flexShrink={0}>
					Repeat every
				</Text>
				<Box flexShrink={0}>{numberSelect}</Box>
				<Box flexShrink={0}>{intervalSelect}</Box>
			</HStack>

			{errorText}
		</VStack>
	);

	if (!showToggle) return repeatFields;

	const toggleFields = (
		<VStack align="stretch" gap={4}>
			<HStack justify="space-between" align="center" gap={4}>
				<VStack align="start" gap={1} flex={1}>
					<Text fontSize="md" fontWeight="medium">
						{COMPOSER_REPEAT_WHEN_COMPLETED_LABEL}
					</Text>
				</VStack>

				<Switch.Root
					checked={isRecurring}
					onCheckedChange={(details) => handleRecurringChange(details.checked as boolean)}
					size="md"
					colorPalette="brand"
				>
					{/* The heading beside it is plain text, not a bound label, so the control
					    carries its own name. */}
					<Switch.HiddenInput aria-label={COMPOSER_REPEAT_WHEN_COMPLETED_LABEL} />
					<Switch.Control />
				</Switch.Root>
			</HStack>

			{isRecurring && repeatFields}
		</VStack>
	);

	if (!framed) return toggleFields;

	return (
		<Panel px={4} py={5}>
			{toggleFields}
		</Panel>
	);
};

export default FrequencySelector;
